<template>
  <div>
    <v-dialog
      width="600px"
      v-model="showDialog"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          v-if="minifiedIcon"
          outlined
          icon
          color="primary"
          @click="loadPopupDialog"
        >
          <v-icon :color="$color.coro.purple">mdi-close</v-icon>
        </v-btn>
        <v-btn
          v-else
          x-large
          text
          v-bind="attrs"
          v-on="on"
          @click="loadPopupDialog"
          color="error"
          class="my-2"
        >
          <v-icon class="mr-2">mdi-close-circle-outline</v-icon>
          {{ getCloseButtonName() }}
        </v-btn>
      </template>
      <div class="dashboard-dialog-window">
        <div class="dashboard-dialog-window__message">
          {{
            anyUnsavedChanges
              ? $ucfirst($t('dummy.SDM.close_record_patient_without_save'))
              : $ucfirst($t('dummy.SDM.close_record_patient'))
          }}
        </div>
        <div class="dashboard-dialog-window__actions">
          <v-btn
            x-large
            text
            class="ml-4"
            @click="endCurrentSDMOrFollowUp"
          >
            {{ $ucfirst($t('closeRecord')) }}
          </v-btn>
          <v-btn
            x-large
            text
            class="ml-4"
            @click="showDialog = false"
            color="primary"
          >
            {{ $ucfirst($t('buttons.cancel', {catalog: 'app_mobile'})) }}
          </v-btn>
        </div>
      </div>
    </v-dialog>

    <!-- No prescription warning -->
    <v-dialog
      v-model="noPrescriptionWarning"
      width="600px"
    >
      <div class="dashboard-dialog-window">
        <div class="dashboard-dialog-window__message">
          {{ $ucfirst($t(
            !missingPrescriptions.hasMedicationPrescription
              ? "encounter.noMedicationWarning"
              : !missingPrescriptions.hasExercisePrescription
                ? "encounter.noExerciseWarning"
                : "encounter.noStepGoalWarning"
          )) }}
        </div>
        <div class="dashboard-dialog-window__actions">
          <v-btn
            x-large
            text
            class="ml-4"
            @click="noPrescriptionWarning = false"
            color="primary"
          >
            {{ $ucfirst($t('ok')) }}
          </v-btn>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import gql from "graphql-tag";
import moment from 'moment';

/**
 * Component ClosePatientRecordButton.vue
 *
 * @module ClosePatientRecordButton
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.4] app/src/APP_DASHBOARD/templates/components/Patient/ClosePatientRecordButton.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: 'ClosePatientRecordButton',
  props: {
    minifiedIcon: {
      type: Boolean,
      default: () => false,
    },
  },
  data() {
    return {
      showDialog: false,
      missingQuestionnaires: [],
      noPrescriptionWarning: false,
      missingPrescriptions: {
        hasMedicationPrescription: true,
        hasExercisePrescription: true,
        hasDailyStepGoal: true,
      }
    };
  },
  computed: {
    ...mapGetters({
      currentVersion: 'patientSummary/currentVersion',
      patientId: 'patientSummary/id',
      isSDMStarted: 'followupSession/isSDMStarted',
      isFollowUpStarted: 'followupSession/isFollowUpStarted',
      followUpType: 'followupSession/type',
      isModuleEnabled: "enabledModules/getIsModuleEnabled",
      anyUnsavedChanges: "patientSummary/anyUnsavedChanges"
    }),
  },
  methods: {
    async endCurrentSDMOrFollowUp() {
      this.showDialog = false;
      await this.checkForPrescriptions();
      const missingPrescription = Object.values(this.missingPrescriptions).some(i => !i);
      const followUpType = this.followUpType;

      if (!this.isSDMStarted && !this.isFollowUpStarted) return;

      // Show warning if SDM is opened and there is no:
      // Medication prescription || Exercise prescription || Daily step goal
      if (followUpType === 'SDM' && missingPrescription) {
        this.noPrescriptionWarning = true;
        return;
      }

      if (followUpType === 'FOLLOWUP'){
        this.$toasted.show(this.$t('dummy.SDM.followup_ended', {
          params: {
            patientName: this.$store.getters['patientSummary/studyId'],
          }
        }));
      } else if (followUpType === 'SDM' && this.currentVersion === 7) {
        this.$toasted.show(this.$t('dummy.SDM.sdm_ended_for_v7'));
      } else if (followUpType === 'SDM') {
        this.$toasted.show(this.$t('dummy.SDM.sdm_ended', {
          params: {
            patientName: this.$store.getters['patientSummary/studyId'],
            visitNumber: this.currentVersion,
          },
        }));
      }

      const ended = await this.$store.dispatch('followupSession/end');

      if (ended) {
        // Remove all recent alerts from screen when an SDM encounter is closed (BE will automatically handle open alerts)
        if (followUpType === 'SDM') {
          this.$store.commit('alerts/setRecent', []);
          this.$store.dispatch('alerts/getAlertCount')
        }

        this.$router.push({
          name: 'PatientOverview',
          params : { patientId : this.patientId },
        });
      }
    },
    loadPopupDialog() {
      this.showDialog = true;
    },
    async checkForPrescriptions() {
      const startMovingEnabled = this.isModuleEnabled('STARTMOVING')

      const hasExercisePrescription = !startMovingEnabled
        ? true
        : await this.$app.apolloClient.query({
        query: gql`
          query getPreviousPrescription($patientId: String!) {
              patientPrescriptions(
                functions: { function: patientId, parameters: [$patientId] },
                limit: 1,
              ){
                id
              }
            }
        `,
        variables: {
          patientId: this.patientId,
        },
      }).then(res => {
        if (!res.data) return;
        return !!res.data?.patientPrescriptions[0]?.id
      });

      // chain queries into one request
      const hasMedicationPrescriptionAndDailySportGoal = await this.$app.apolloMobileClient.query({
        query: gql`
          query hasMedicationPrescriptionAndDailySportGoal(
            $patientId: String!,
            $from: String!,
            $until: String!
          ) {
              # medication prescription
              myMedicationSchedules(
                functions: { function: patientId, parameters: [$patientId] }
                conditions: { field: deleted, value: "0" }
              ){
                id
              }
              # daily step goal
              myActivityWeeks(
                functions: { function: patientId, parameters: [$patientId] }
                conditions: { field: activityDate, operator: between, parameters: [$from, $until] }
                limit: 1
              ) {
                edges {
                  node {
                    dailyStepGoalNoSport
                  }
                }
              }
            }
        `,
        variables: {
          patientId: this.patientId,
          from: moment().clone().startOf("isoWeek").format("YYYY-MM-DD"),
          until: moment().clone().endOf("isoWeek").format("YYYY-MM-DD"),
        },
      }).then(res => {
        if (!res.data) return;
        const { myActivityWeeks, myMedicationSchedules } = res.data;
        const dailyStepGoal = myActivityWeeks?.edges[0]?.node?.dailyStepGoalNoSport > 0;
        const medicationSchedule = myMedicationSchedules[0]?.id;

        return {
          hasMedicationPrescription: !!medicationSchedule,
          hasDailyStepGoal: startMovingEnabled ? dailyStepGoal : true
        }
      });

      this.missingPrescriptions = Object.assign(this.missingPrescriptions, {
        hasExercisePrescription,
        ...hasMedicationPrescriptionAndDailySportGoal
      });
    },
    getCloseButtonName() {
      if (this.isFollowUpStarted) {
        return this.$ucfirst(this.$t('closeRecord'))
      } else if (this.isSDMStarted) {
        return this.$ucfirst(this.$t('encounter.endEncounter'))
      }
    }
  },
}
</script>
